// @flow
/** @jsx jsx */

import { Fragment } from 'react';
import { css, jsx } from '@emotion/core';
import { capitalize } from 'lodash';

import {
  type BankFormFields,
  bankAccountTypes,
  bankOwnershipTypes,
} from '@zapier/payment/src/types';
import { TextInput } from '@zapier/design-system';
import SensitiveData from '@zapier/payment/src/components/SensitiveData';
import PaymentFormField from '@zapier/payment/src/components/PaymentFormField';
import PostalCodeInput from '@zapier/payment/src/components/PostalCodeInput';
import StateSelect from '@zapier/payment/src/components/StateSelect';

import { useSize } from '@zapier/payment/src/hooks/useSize';
import {
  getValue,
  handleChange,
  hasError,
} from '@zapier/payment/src/utils/formHelpers';

import RadioButtonGroup from '../RadioButtonGroup';

const accountTypeOptions = [
  {
    key: bankAccountTypes.checking,
    displayText: capitalize(bankAccountTypes.checking),
  },
  {
    key: bankAccountTypes.savings,
    displayText: capitalize(bankAccountTypes.savings),
  },
];

const ownershipTypeOptions = [
  {
    key: bankOwnershipTypes.business,
    displayText: capitalize(bankOwnershipTypes.business),
  },
  {
    key: bankOwnershipTypes.personal,
    displayText: capitalize(bankOwnershipTypes.personal),
  },
];

const rootStyle = css`
  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(4, 1fr);
`;

type Props = {
  fields: BankFormFields,
  onChangeField: (field: string, value: string) => void,
};

const BankAccountFormFields = ({ fields, onChangeField }: Props) => {
  const size = useSize();
  const isSmall = size === 'small';

  const ownershipType = getValue('ownershipType', fields);
  return (
    <div css={rootStyle}>
      <PaymentFormField
        id="routingNumber"
        label="Routing Number"
        span={isSmall ? 4 : 2}
      >
        <SensitiveData>
          <TextInput
            hasError={hasError('routingNumber', fields)}
            maxLength={9}
            onChange={handleChange(onChangeField, 'routingNumber', {
              ignoreWhitespace: true,
            })}
            type="tel"
            value={getValue('routingNumber', fields)}
          />
        </SensitiveData>
      </PaymentFormField>

      <PaymentFormField
        id="accountNumber"
        label="Account Number"
        span={isSmall ? 4 : 2}
      >
        <SensitiveData>
          <TextInput
            hasError={hasError('accountNumber', fields)}
            onChange={handleChange(onChangeField, 'accountNumber', {
              ignoreWhitespace: true,
            })}
            type="tel"
            value={getValue('accountNumber', fields)}
          />
        </SensitiveData>
      </PaymentFormField>

      <PaymentFormField
        id="accountType"
        isGroup={true}
        label="Account type"
        span={isSmall ? 4 : 2}
      >
        <RadioButtonGroup
          id="accountType"
          onSelectOption={handleChange(onChangeField, 'accountType')}
          options={accountTypeOptions}
          selectedOption={getValue('accountType', fields)}
        />
      </PaymentFormField>

      <PaymentFormField
        id="ownershipType"
        isGroup={true}
        label="Ownership type"
        span={isSmall ? 4 : 2}
      >
        <RadioButtonGroup
          id="ownershipType"
          onSelectOption={handleChange(onChangeField, 'ownershipType')}
          options={ownershipTypeOptions}
          selectedOption={ownershipType}
        />
      </PaymentFormField>

      {ownershipType === bankOwnershipTypes.personal && (
        <Fragment>
          <PaymentFormField id="firstName" label="First name" span={2}>
            <TextInput
              hasError={hasError('firstName', fields)}
              onChange={handleChange(onChangeField, 'firstName')}
              value={getValue('firstName', fields)}
            />
          </PaymentFormField>
          <PaymentFormField id="lastName" label="Last name" span={2}>
            <TextInput
              hasError={hasError('lastName', fields)}
              onChange={handleChange(onChangeField, 'lastName')}
              value={getValue('lastName', fields)}
            />
          </PaymentFormField>
        </Fragment>
      )}

      {ownershipType === bankOwnershipTypes.business && (
        <PaymentFormField id="businessName" label="Business name" span={4}>
          <TextInput
            hasError={hasError('businessName', fields)}
            onChange={handleChange(onChangeField, 'businessName')}
            value={getValue('businessName', fields)}
          />
        </PaymentFormField>
      )}

      <PaymentFormField
        id="streetAddress"
        label="Street address"
        noMargin={true}
        span={4}
      >
        <SensitiveData>
          <TextInput
            hasError={hasError('streetAddress', fields)}
            onChange={handleChange(onChangeField, 'streetAddress')}
            placeholder="Street and number, P.O. box, c/o."
            value={getValue('streetAddress', fields)}
          />
        </SensitiveData>
      </PaymentFormField>
      <PaymentFormField id="extendedAddress" span={4}>
        <SensitiveData>
          <TextInput
            hasError={hasError('extendedAddress', fields)}
            onChange={handleChange(onChangeField, 'extendedAddress')}
            placeholder="Apartment, suite, unit, building, floor, etc."
            value={getValue('extendedAddress', fields)}
          />
        </SensitiveData>
      </PaymentFormField>
      <PaymentFormField id="locality" label="City" span={isSmall ? 4 : 2}>
        <TextInput
          hasError={hasError('locality', fields)}
          onChange={handleChange(onChangeField, 'locality')}
          value={getValue('locality', fields)}
        />
      </PaymentFormField>
      <PaymentFormField id="region" label="State" span={isSmall ? 4 : 1}>
        <StateSelect
          hasError={hasError('region', fields)}
          onChange={handleChange(onChangeField, 'region')}
          value={getValue('region', fields)}
        />
      </PaymentFormField>
      <PaymentFormField id="postalCode" label="Zip code" span={isSmall ? 4 : 1}>
        <PostalCodeInput
          hasError={hasError('postalCode', fields)}
          onChange={handleChange(onChangeField, 'postalCode')}
          value={getValue('postalCode', fields)}
        />
      </PaymentFormField>
    </div>
  );
};

export default BankAccountFormFields;
